import { create } from 'zustand';
import { ApiRequest } from '@/utils/axios';
import { ICase, ICaseBattleGame, ITeam } from '@/utils/types/ICaseBattle';
import { ICaseBattlePlayer, IRoll } from '@/utils/types/ICaseBattlePlayer';

export interface CasebattlesState {
    minbet: number;
    games: Map<number, ICaseBattleGame>;
    cases: ICase[];
    selected: Map<ICase['id'], number>;
    historyGameIds: Set<number>;
    activeGameIds: Set<number>;
    activeGames: () => ICaseBattleGame[];
    historyGames: () => ICaseBattleGame[];
    addParticipant: (team: ITeam, participant: ICaseBattlePlayer, casebattleId: number) => void;
    loadActiveGames: () => Promise<void>;
    loadHistoryGames: () => Promise<void>;
    addGame: (game: ICaseBattleGame) => void;
    addRoll: (rolls: IRoll[], casebattleId: number) => void;
    deleteGame: (gameId: number) => void;
    clear: () => void;
    setSelected: (cases: Map<ICase['id'], number>) => void;
    getCases: () => Promise<void>;
    getCase: (id: number) => ICase;
    setWinnerByGameId: (gameId: number, teamWinners: ITeam[]) => void;
    getHistoryGame: (id: number) => Promise<ICaseBattleGame>;
    getGame: (id: number) => ICaseBattleGame | undefined;
    startGame: (id: number) => void;
    getItemPercent: (caseId: number, itemId: number) => string;
}

export const casebattlesStore = create<CasebattlesState>((set, get) => ({
    minbet: 10,
    games: new Map(),
    cases: [],
    activeGameIds: new Set(),
    historyGameIds: new Set<number>(),
    selected: new Map(),
    setSelected(cases) {
        set(() => {
            return { selected: cases };
        });
    },

    historyGames() {
        return Array.from(get().historyGameIds)
            .sort((a, b) => b - a)
            .map((id) => get().games.get(id)!);
    },
    activeGames() {
        return Array.from(get().activeGameIds)
            .map((id) => get().games.get(id)!)
            .filter((game) => game !== undefined && game.status != 'FINISHED')
            .sort((a, b) => {
                const statusOrder = {
                    STARTED: -1,
                    PENDING: 0,
                    FINISHED: 1,
                };

                const statusComparison = statusOrder[a.status] - statusOrder[b.status];
                if (statusComparison !== 0) {
                    return statusComparison;
                }
                return 1;
            });
    },

    getCase: (id) => {
        return get().cases.find((c) => c.id === id)!;
    },
    getCases: async () => {
        if (!get().cases.length) {
            const {
                data: { response },
            } = await ApiRequest.get('/cases/list');
            set(() => ({
                cases: response,
            }));
        }
    },
    getItemPercent: (caseId, itemId) => {
        const foundedCase = get().cases.find((c) => c.id == caseId);

        if (foundedCase) {
            return (
                (foundedCase.items.find((i) => i.itemId == itemId)?.probability || 0) * 100
            ).toFixed(2);
        } else return '0';
    },

    loadActiveGames: async () => {
        try {
            const {
                data: { response },
            } = await ApiRequest.get('/casebattle/list');

            set(() => {
                const newGames = new Map<number, ICaseBattleGame>();
                const newActiveGameIds = new Set<number>();

                response.forEach((game: ICaseBattleGame) => {
                    newGames.set(game.id, {
                        ...game,
                        cases: game.cases.sort((a, b) => a.id - b.id),
                    });
                    newActiveGameIds.add(game.id);
                });

                return {
                    games: newGames,
                    activeGameIds: newActiveGameIds,
                };
            });
        } catch (e) {
            console.log(e);
        }
    },
    loadHistoryGames: async () => {
        try {
            const res = await ApiRequest.get<{ response: ICaseBattleGame[] }>(
                '/casebattle/history/all'
            );

            set((state) => {
                const games = new Map(state.games);
                const historyGameIds = new Set(state.historyGameIds);

                for (const game of res.data.response) {
                    games.set(game.id, game);
                    historyGameIds.add(game.id);
                }

                return {
                    games,
                    historyGameIds,
                };
            });
        } catch (e) {
            console.log(e);
        }
    },

    startGame: (id) => {
        set((state) => {
            const games = new Map(state.games);
            const game = games.get(id);
            if (!game) return state;

            const updatedGame: ICaseBattleGame = {
                ...game,
                status: 'STARTED',
                startedAt: new Date(),
            };

            games.set(game.id, updatedGame);
            return { games };
        });
    },
    getGame: (id) => {
        return (
            get().games.get(id) ||
            get()
                .historyGames()
                .find((g) => g?.id === id)
        );
    },
    getHistoryGame: async (id) => {
        const {
            data: { response },
        } = await ApiRequest.get<{ response: ICaseBattleGame }>(`/casebattle/details/${id}`);

        return response;
    },
    setWinnerByGameId: (gameId, teamWinners) => {
        set((state) => {
            const games = new Map(state.games);
            if (games.has(gameId)) {
                const game = games.get(gameId)!;
                game.teamWinners = teamWinners;
                games.set(gameId, game);
            }
            return { games };
        });
    },
    addGame(game) {
        const alreadyInList = get().games.has(game.id);
        set((state) => {
            const games = new Map(state.games);
            const activeGameIds = new Set(state.activeGameIds);

            games.set(game.id, game);

            if (!alreadyInList) {
                activeGameIds.add(game.id);
            }

            return { games, activeGameIds };
        });
    },
    addParticipant(team, participant, casebattleId) {
        set((state) => {
            const games = new Map(state.games);
            const game = games.get(casebattleId);
            if (!game) return state;

            const updatedTeams = game.teams.map((t) => {
                if (t.id === team.id) {
                    return {
                        ...t,
                        participants: [...t.participants, participant],
                    };
                }
                return t;
            });

            const updatedGame: ICaseBattleGame = {
                ...game,
                teams: updatedTeams,
            };

            games.set(game.id, updatedGame);
            return { games };
        });
    },
    addRoll(rolls, casebattleId) {
        set((state) => {
            const games = new Map(state.games);
            const game = games.get(casebattleId);
            if (!game) return state;

            const updatedTeams = game.teams.map((team) => {
                const updatedParticipants = team.participants.map((participant) => {
                    const roll = rolls.find((roll) => roll.participantId === participant.id);

                    return {
                        ...participant,
                        rolls: roll ? [...(participant.rolls || []), roll] : participant.rolls,
                    };
                });

                return {
                    ...team,
                    participants: updatedParticipants || [],
                };
            });

            const updatedGame = {
                ...game,
                teams: updatedTeams,
            };

            games.set(game.id, updatedGame);

            return { games };
        });
    },

    deleteGame(gameId: number) {
        set((state) => {
            const games = new Map(state.games);
            const activeGameIds = new Set(state.activeGameIds);
            const historyGameIds = new Set(state.historyGameIds);

            activeGameIds.delete(gameId);
            historyGameIds.delete(gameId);
            games.delete(gameId);

            return { games, activeGameIds, historyGameIds };
        });
    },
    clear() {
        set(() => {
            return {
                games: new Map(),
            };
        });
    },
}));
